"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  X,
  Check,
  ListPlus,
  ExternalLink,
  Store as StoreIcon,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatCurrency } from "@/lib/utils";
import { GoLink } from "@/components/go-link";
import { shouldSkipOptimization } from "@/lib/image-host";
import { AddToListModal } from "@/components/sections/comparador/add-to-list-modal";
import { useFlag } from "@/lib/flags-context";
import { productHref } from "@/lib/product-href";

/**
 * "Ir a la tienda" intentionally lives ONLY inside the preview modal — the
 * carousels stay focused on exploration/compare actions, no store outclick.
 */

export interface CarouselProduct {
  id: string;
  globalProductId: string;
  /** Para enlazar a la URL canónica /productos/<numericId>-<slug>. */
  numericId?: number;
  name: string;
  brand?: string;
  imageUrl?: string;
  category: string;
  soldByWeight?: boolean;
  lowestPrice: number;
  previousPrice?: number;
  discount?: number;
  rating?: number;
  storeCount: number;
  topPrices: { storeId: string; storeName: string; price: number; originalPrice?: number; shipping?: string; url?: string }[];
}

export interface SponsoredItem {
  kind: "ad";
  id: string;
  title: string;
  imageUrl: string;
  href: string;
  sponsorLabel: string;
  cta?: string;
}

export type CarouselItem = ({ kind: "product" } & CarouselProduct) | SponsoredItem;

export interface CarouselSectionData {
  id: string;
  title: string;
  subtitle: string;
  icon: string; // emoji removed: use a key understood by client → icon component
  items: CarouselItem[];
  /** Optional CTA chip rendered in the section header (right side, before arrows). */
  cta?: { href: string; label: string };
  /** When true, hides the carousel track but keeps header (used by paused sections). */
  paused?: boolean;
}

const STAR_SVG = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className="w-3.5 h-3.5 text-amber-400"
    aria-hidden
  >
    <path d="M12 2.25l3.09 6.26 6.91 1-5 4.87 1.18 6.87L12 17.77l-6.18 3.48L7 14.38l-5-4.87 6.91-1L12 2.25z" />
  </svg>
);

interface Props {
  sections: CarouselSectionData[];
}

export function ProductCarousels({ sections }: Props) {
  const [preview, setPreview] = useState<CarouselProduct | null>(null);
  const [listModalProduct, setListModalProduct] = useState<CarouselProduct | null>(null);
  const [recentlyAdded, setRecentlyAdded] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<string | null>(null);
  const shoppingListEnabled = useFlag("products.shoppingList");
  // The "Ir a la tienda" affordance is now preview-modal-only.
  const goToStoreEnabled = useFlag("products.goToStoreCta");

  const openListModal = (product: CarouselProduct) => {
    setListModalProduct(product);
  };

  const onListModalClose = () => {
    // We can't reliably know whether the user added the item here, so we keep
    // the indicator update for when explicit success is reported below.
    setListModalProduct(null);
  };

  const handleAddedToList = (product: CarouselProduct) => {
    setRecentlyAdded((prev) => {
      const next = new Set(prev);
      next.add(product.id);
      return next;
    });
    showToast(`Añadido a tu lista: ${product.name}`);
  };

  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showToast = (msg: string) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2400);
  };

  return (
    <div className="space-y-12">
      {sections.map((s) => (
        <CarouselSectionView
          key={s.id}
          section={s}
          onPreview={setPreview}
          onToggleList={shoppingListEnabled ? openListModal : undefined}
          listed={recentlyAdded}
        />
      ))}

      {preview && (
        <PreviewModal
          product={preview}
          onClose={() => setPreview(null)}
          onToggleList={shoppingListEnabled ? openListModal : undefined}
          listed={recentlyAdded.has(preview.id)}
          goToStoreEnabled={goToStoreEnabled}
        />
      )}

      {shoppingListEnabled && listModalProduct && (
        <AddToListModal
          open={true}
          onClose={onListModalClose}
          productId={listModalProduct.id}
          productName={listModalProduct.name}
          soldByWeight={listModalProduct.soldByWeight}
          onAdded={() => handleAddedToList(listModalProduct)}
        />
      )}

      {/* Toast */}
      {toast && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-60 bg-foreground text-background text-sm font-semibold px-4 py-2.5 rounded-full shadow-2xl flex items-center gap-2 animate-in fade-in slide-in-from-bottom-4 duration-200"
          role="status"
        >
          <Check className="w-4 h-4 text-emerald-400" />
          {toast}
        </div>
      )}
    </div>
  );
}

function CarouselSectionView({
  section,
  onPreview,
  onToggleList,
  listed,
}: {
  section: CarouselSectionData;
  onPreview: (p: CarouselProduct) => void;
  onToggleList?: (p: CarouselProduct) => void;
  listed: Set<string>;
}) {
  const trackRef = useRef<HTMLDivElement>(null);

  const scroll = (dir: "prev" | "next") => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.querySelector<HTMLElement>("[data-card]");
    const step = card ? (card.offsetWidth + 16) * 2 : track.clientWidth * 0.8;
    track.scrollBy({ left: dir === "next" ? step : -step, behavior: "smooth" });
  };

  return (
    <section>
      <div className="flex items-end justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xl sm:text-2xl" aria-hidden>{section.icon}</span>
            <h2 className="text-lg sm:text-2xl font-black leading-tight tracking-tight truncate">
              {section.title}
            </h2>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground truncate mt-0.5">
            {section.subtitle}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {section.cta && (
            <Link
              href={section.cta.href}
              className="hidden sm:inline-flex items-center gap-1.5 px-3.5 h-9 rounded-full border bg-card hover:bg-primary hover:text-primary-foreground hover:border-primary text-xs font-bold transition-colors"
            >
              {section.cta.label}
              <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          )}
          <button
            type="button"
            onClick={() => scroll("prev")}
            aria-label="Anterior"
            className="w-9 h-9 rounded-full border bg-background hover:bg-muted shadow-xs flex items-center justify-center transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => scroll("next")}
            aria-label="Siguiente"
            className="w-9 h-9 rounded-full border bg-background hover:bg-muted shadow-xs flex items-center justify-center transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {section.paused ? (
        <div className="rounded-2xl border border-dashed bg-muted/20 p-8 text-center text-xs text-muted-foreground">
          Estamos preparando los productos de esta sección.
        </div>
      ) : (
      <div
        ref={trackRef}
        className="flex gap-4 overflow-x-auto scroll-smooth pb-2 snap-x scrollbar-hide"
        style={{ scrollbarWidth: "none" }}
      >
        {section.items.map((item) =>
          item.kind === "ad" ? (
            <SponsoredCard key={item.id} ad={item} />
          ) : (
            <ProductCard
              key={item.id}
              product={item}
              onPreview={onPreview}
              onToggleList={onToggleList}
              listed={listed.has(item.id)}
            />
          )
        )}
      </div>
      )}

      {section.cta && (
        <div className="sm:hidden mt-2">
          <Link
            href={section.cta.href}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border bg-card hover:bg-primary hover:text-primary-foreground text-xs font-bold transition-colors"
          >
            {section.cta.label}
            <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}
    </section>
  );
}

function ProductCard({
  product,
  onPreview,
  onToggleList,
  listed,
}: {
  product: CarouselProduct;
  onPreview: (p: CarouselProduct) => void;
  onToggleList?: (p: CarouselProduct) => void;
  listed: boolean;
}) {
  const router = useRouter();
  const goToProduct = () => router.push(productHref(product));

  return (
    <div
      data-card
      className="group relative shrink-0 snap-start w-[170px] sm:w-[200px] flex flex-col"
    >
      {/* Image — clickable div (NOT a Link) so buttons can sit above without conflict */}
      <div
        role="button"
        tabIndex={0}
        onClick={goToProduct}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            goToProduct();
          }
        }}
        className="relative aspect-square overflow-hidden cursor-pointer"
        style={{
          border: "6px solid transparent",
          borderRadius: "18px",
          background: "transparent",
        }}
      >
        {product.imageUrl ? (
          <Image
            src={product.imageUrl}
            alt={product.name}
            fill
            className="object-contain p-2 group-hover:scale-105 transition-transform duration-300 pointer-events-none"
            sizes="200px"
            unoptimized={shouldSkipOptimization(product.imageUrl)}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground text-xs">
            Sin imagen
          </div>
        )}

        {product.discount ? (
          <div className="absolute top-2 left-0 pointer-events-none">
            <div className="bg-red-600 text-white text-[11px] font-black px-2.5 py-1 shadow-md rounded-r-md">
              -{product.discount}%
            </div>
          </div>
        ) : null}

        {/* Action buttons — direct children of the clickable div, but stop propagation */}
        <div className="absolute top-3 right-3 flex flex-col gap-1.5 z-20">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onPreview(product);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            aria-label="Vista previa"
            className="w-9 h-9 rounded-full bg-white border shadow-md flex items-center justify-center hover:bg-primary hover:text-primary-foreground active:scale-95 transition-all cursor-pointer"
          >
            <Eye className="w-4 h-4 pointer-events-none" />
          </button>
          {onToggleList && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onToggleList(product);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              aria-label={listed ? "En tu lista" : "Agregar a lista de compras"}
              className={`w-9 h-9 rounded-full border shadow-md flex items-center justify-center active:scale-95 transition-all cursor-pointer ${
                listed
                  ? "bg-emerald-500 text-white border-emerald-500"
                  : "bg-white hover:bg-primary hover:text-primary-foreground"
              }`}
            >
              {listed ? (
                <Check className="w-4 h-4 pointer-events-none" />
              ) : (
                <ListPlus className="w-4 h-4 pointer-events-none" />
              )}
            </button>
          )}
        </div>
      </div>

      <div className="px-1 pt-2 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <Link
            href={productHref(product)}
            className="text-sm font-semibold leading-snug line-clamp-2 min-h-10 flex-1 group-hover:text-primary transition-colors"
          >
            {product.name}
          </Link>
          {typeof product.rating === "number" ? (
            <span className="inline-flex items-center gap-0.5 text-xs font-bold text-foreground/80 shrink-0 mt-0.5">
              {STAR_SVG}
              {product.rating.toFixed(1)}
            </span>
          ) : null}
        </div>

        <div className="mt-1.5">
          <p className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider leading-tight">
            Desde
          </p>
          <div className="flex items-baseline gap-1.5 flex-wrap">
            <span className="text-lg font-black text-emerald-600 leading-none">
              {formatCurrency(product.lowestPrice)}
            </span>
            {product.previousPrice ? (
              <span className="text-xs text-muted-foreground line-through">
                {formatCurrency(product.previousPrice)}
              </span>
            ) : null}
          </div>
          {product.discount ? (
            <span className="inline-block mt-1 text-[10px] font-black text-rose-600">
              {product.discount}% OFF
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function SponsoredCard({ ad }: { ad: SponsoredItem }) {
  return (
    <a
      data-card
      href={ad.href}
      target="_blank"
      rel="noopener sponsored"
      className="group relative shrink-0 snap-start w-[170px] sm:w-[200px] flex flex-col"
    >
      <div className="relative">
        <div
          className="block relative aspect-square overflow-hidden"
          style={{
            border: "6px solid transparent",
            borderRadius: "18px",
            background: "transparent",
          }}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={ad.imageUrl}
            alt={ad.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-linear-to-t from-black/60 via-black/0 to-black/0" />
          <div className="absolute top-2 left-0 bg-amber-400 text-amber-900 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-r shadow-md">
            Patrocinado
          </div>
          <div className="absolute bottom-2 left-2 right-2 text-white">
            <p className="text-[10px] uppercase tracking-wider font-semibold opacity-80">
              {ad.sponsorLabel}
            </p>
            <p className="text-sm font-black leading-tight line-clamp-2">{ad.title}</p>
          </div>
        </div>
      </div>
      <div className="px-1 pt-2 flex items-center justify-between gap-2">
        <span className="text-xs font-bold text-primary inline-flex items-center gap-1 group-hover:underline">
          {ad.cta ?? "Ver promoción"}
          <ExternalLink className="w-3 h-3" />
        </span>
        <span className="text-[10px] text-muted-foreground">Anuncio</span>
      </div>
    </a>
  );
}

function PreviewModal({
  product,
  onClose,
  onToggleList,
  listed,
  goToStoreEnabled,
}: {
  product: CarouselProduct;
  onClose: () => void;
  onToggleList?: (p: CarouselProduct) => void;
  listed: boolean;
  goToStoreEnabled: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const topThree = product.topPrices.slice(0, 3);
  const cheapest = product.topPrices[0];
  const showGoToStore = goToStoreEnabled && Boolean(cheapest?.url);
  const cheapestForLink = cheapest;

  return (
    <div
      className="fixed inset-0 z-100 bg-black/60 backdrop-blur-xs flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-background rounded-t-3xl sm:rounded-3xl w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-background/95 backdrop-blur-sm border-b z-10 flex items-center justify-between px-5 py-3">
          <h3 className="font-bold text-sm">Vista previa</h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full border hover:bg-muted flex items-center justify-center"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="grid sm:grid-cols-[180px_1fr] gap-4 sm:gap-5">
            <div
              className="relative aspect-square overflow-hidden shrink-0"
              style={{ border: "6px solid transparent", borderRadius: "18px", background: "transparent" }}
            >
              {product.imageUrl ? (
                <Image
                  src={product.imageUrl}
                  alt={product.name}
                  fill
                  className="object-contain p-2"
                  sizes="180px"
                  unoptimized={shouldSkipOptimization(product.imageUrl)}
                />
              ) : null}
              {product.discount ? (
                <div className="absolute top-2 left-0 bg-red-600 text-white text-[11px] font-black px-2.5 py-1 shadow-md rounded-r-md">
                  -{product.discount}%
                </div>
              ) : null}
            </div>

            <div className="min-w-0">
              {product.brand && (
                <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">
                  {product.brand}
                </p>
              )}
              <div className="flex items-start justify-between gap-2">
                <h2 className="text-lg sm:text-xl font-black leading-tight">{product.name}</h2>
                {typeof product.rating === "number" ? (
                  <span className="inline-flex items-center gap-0.5 text-sm font-bold shrink-0 mt-0.5">
                    {STAR_SVG}
                    {product.rating.toFixed(1)}
                  </span>
                ) : null}
              </div>

              <div className="mt-3 pt-3 border-t">
                <p className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">
                  Desde
                </p>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-3xl font-black text-emerald-600">
                    {formatCurrency(product.lowestPrice)}
                  </span>
                  {product.previousPrice ? (
                    <span className="text-sm text-muted-foreground line-through">
                      {formatCurrency(product.previousPrice)}
                    </span>
                  ) : null}
                  {product.discount ? <Badge variant="hot">{product.discount}% OFF</Badge> : null}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Disponible en{" "}
                  <span className="font-bold text-foreground">{product.storeCount}</span> tiendas
                </p>
              </div>
            </div>
          </div>

          {topThree.length > 0 && (
            <div>
              <h4 className="text-sm font-bold mb-2">Top 3 mejores precios</h4>
              <div className="space-y-2">
                {topThree.map((p, idx) => (
                  <div
                    key={`${p.storeId}-${idx}`}
                    className="flex items-center justify-between gap-3 p-3 rounded-xl border bg-muted/30"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-xs shrink-0 ${
                          idx === 0
                            ? "bg-yellow-400 text-yellow-900"
                            : idx === 1
                              ? "bg-zinc-300 text-zinc-800"
                              : "bg-amber-700/80 text-white"
                        }`}
                      >
                        {idx + 1}
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold text-sm truncate">{p.storeName}</p>
                        {p.shipping && (
                          <p className="text-[11px] text-muted-foreground truncate">{p.shipping}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-base font-black text-emerald-600">
                        {formatCurrency(p.price)}
                      </p>
                      {p.originalPrice && p.originalPrice > p.price && (
                        <p className="text-[11px] text-muted-foreground line-through">
                          {formatCurrency(p.originalPrice)}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2">
            {onToggleList && (
              <button
                type="button"
                onClick={() => onToggleList(product)}
                className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm transition-colors ${
                  listed
                    ? "bg-emerald-500 text-white hover:bg-emerald-600"
                    : "border bg-background hover:bg-muted"
                }`}
              >
                {listed ? <Check className="w-4 h-4" /> : <ListPlus className="w-4 h-4" />}
                {listed ? "En tu lista" : "Agregar a lista de compras"}
              </button>
            )}
            <Link
              href={productHref(product)}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Ver comparativa completa
            </Link>
            {showGoToStore && cheapestForLink?.url && (
              <GoLink
                storeId={cheapestForLink.storeId}
                url={cheapestForLink.url}
                productId={product.id}
                tab="comparacion"
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
              >
                <StoreIcon className="w-4 h-4" />
                Ir a la tienda
              </GoLink>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
